import { formatISO } from "date-fns";
import { nanoid } from "nanoid/non-secure";
import { z } from "zod";

import type {
	GetWorkflowTemplateResponseJSON,
	WorkflowTemplateSignatureMapJSON,
	WorkflowTemplateUserMapJSON,
} from "../../../api/services/defs/workflow-templates";
import type { LaunchWorkflowPayload, LaunchWorkflowTodoJSON } from "../../../api/services/defs/workflows";

/**
 * Client limits mirror the legacy page's `Config` (250-char names, 50 to-dos) and the
 * marshmallow launch schema's floors (at least one to-do, days >= 1) so nothing valid
 * here can 422 server-side. The server's hard name ceiling is 280
 * (`MAX_WORKFLOW_TEXT_LENGTH`); 250 is kept as the stricter legacy client cap.
 */
export const NAME_MAX_LENGTH = 250;
export const TODOS_MAX_COUNT = 50;

/** The design's single required-field message, reused by every step's validation. */
export const REQUIRED_FIELD_MESSAGE = "This is a required field";
const REQUIRED = REQUIRED_FIELD_MESSAGE;

/**
 * Field error for an assignee/signer the server reported as deactivated. The to-dos step
 * shows this class of error immediately rather than waiting for a Next attempt, so it
 * has to compare against the constant — never the copy.
 */
export const DEACTIVATED_ERROR = "Deactivated";

/** The three wildcard assignees the server accepts in place of a user id. */
export const ASSIGNEE_WILDCARDS = [
	{ id: "self", name: "Employee", avatarInitials: "E" },
	{ id: "manager", name: "Employee's Manager", avatarInitials: "EM" },
	{ id: "other", name: "Other Employee", avatarInitials: "OE" },
] as const;

/** Signature steps can't be "other" — the launch schema rejects unassigned signers. */
export const SIGNER_WILDCARDS = ASSIGNEE_WILDCARDS.filter((wildcard) => wildcard.id !== "other");

export type AssigneeWildcard = (typeof ASSIGNEE_WILDCARDS)[number];

export const getAssigneeWildcard = (id: string): AssigneeWildcard | undefined =>
	ASSIGNEE_WILDCARDS.find((wildcard) => wildcard.id === id);

export type AssigneeValue = {
	/** A user id or one of the wildcard references "self" | "manager" | "other". */
	id: string;
	name: string;
	/** False only for template actors the server reported as deactivated. */
	active?: boolean;
};

export type SignatureTemplateRef = {
	id: string;
	name: string;
};

/** One editable to-do row of the launch wizard's third step. */
export type LaunchTodoRow = {
	/**
	 * Client-only row key: a `nanoid`, NOT a Mongo id, and stripped from the launch
	 * payload. Rows are identified by it so edits and removals survive re-renders.
	 */
	_id: string;
	name: string;
	/** Null while unset, and always null when a signature template is attached. */
	assignee: AssigneeValue | null;
	daysToComplete: number | null;
	signatureTemplate: SignatureTemplateRef | null;
	/** One entry per signature step; null entries still need a signer picked. */
	signers: (AssigneeValue | null)[];
};

/** Step ids double as the `wizardData` keys the preview step reads. */
export const GENERAL_INFO_STEP_ID = "general-info";
export const GENERAL_INFO_STEP_TITLE = "Workflow Settings";
export const EMPLOYEES_STEP_ID = "employees";
export const EMPLOYEES_STEP_TITLE = "Add Subjects";
export const TODOS_STEP_ID = "todos";
export const TODOS_STEP_TITLE = "Manage To-Dos";
export const PREVIEW_STEP_ID = "preview";
export const PREVIEW_STEP_TITLE = "Preview and Finalize";

export const GeneralInfoSchema = z.object({
	template: z
		.object({ id: z.string(), name: z.string() })
		.nullable()
		.refine((value) => value !== null, REQUIRED),
	workflowName: z
		.string()
		.trim()
		.min(1, REQUIRED)
		.max(NAME_MAX_LENGTH, `Workflow name must be ${NAME_MAX_LENGTH} characters or less`),
	startDate: z
		.date()
		.nullable()
		.refine((value) => value !== null, REQUIRED),
});

export type GeneralInfoData = z.infer<typeof GeneralInfoSchema>;

export const getGeneralInfoDefaults = (): GeneralInfoData => ({
	template: null,
	workflowName: "",
	startDate: null,
});

export type EmployeesStepData = {
	userSelectionId: string;
	selectedCount: number;
};

/**
 * Typed reads out of the Wizard's `Record<string, unknown>` step bag. The webserver
 * React app isn't type-checked in CI, so a mistyped step id would otherwise compile
 * fine and surface as a Launch button that silently does nothing — these keep the cast
 * in one place, next to the step ids it depends on.
 */
export const readGeneralInfo = (wizardData: Record<string, unknown>): GeneralInfoData | undefined =>
	wizardData[GENERAL_INFO_STEP_ID] as GeneralInfoData | undefined;

export const readEmployeesStepData = (wizardData: Record<string, unknown>): EmployeesStepData | undefined =>
	wizardData[EMPLOYEES_STEP_ID] as EmployeesStepData | undefined;

export const buildEmptyTodoRow = (): LaunchTodoRow => ({
	_id: nanoid(),
	name: "",
	assignee: null,
	daysToComplete: null,
	signatureTemplate: null,
	signers: [],
});

/**
 * Map a fetched template's to-dos onto launch rows, resolving actor ids through the
 * wildcards and `display_user_map`, and signature template ids through
 * `signature_request_map`. Deactivated template actors stay selected but carry
 * `active: false` so the step can demand a replacement (mirrors the legacy page's
 * deactivated-actor gate). Signature steps whose signer is "other" start unset — the
 * launch schema rejects unassigned signers, so one must be picked before launch.
 */
export const toLaunchTodoRows = (
	todos: GetWorkflowTemplateResponseJSON["template"]["todos"],
	userMap: WorkflowTemplateUserMapJSON,
	signatureMap: WorkflowTemplateSignatureMapJSON
): LaunchTodoRow[] =>
	todos.map((todo) => {
		const signatureTemplateId =
			typeof todo.documentSigning === "object" && todo.documentSigning !== null
				? todo.documentSigning.id
				: todo.documentSigning;

		let assignee: LaunchTodoRow["assignee"] = null;
		if (todo.actor && !signatureTemplateId) {
			const wildcard = getAssigneeWildcard(todo.actor);
			assignee = wildcard
				? { id: wildcard.id, name: wildcard.name }
				: {
						id: todo.actor,
						name: userMap[todo.actor]?.name ?? "Unknown employee",
						active: userMap[todo.actor]?.active ?? true,
					};
		}

		const signatureEntry = signatureTemplateId ? signatureMap[signatureTemplateId] : undefined;

		return {
			_id: nanoid(),
			name: todo.label ?? "",
			assignee,
			daysToComplete: typeof todo.daysToComplete === "number" ? todo.daysToComplete : null,
			signatureTemplate: signatureTemplateId
				? { id: signatureTemplateId, name: signatureEntry?.name ?? "Signature Template" }
				: null,
			signers: (signatureEntry?.signers ?? []).map((signer) => toSignerValue(signer, userMap)),
		};
	});

/** Resolve a raw signer reference ("self" | "manager" | "other" | user id) to a picker value. */
export const toSignerValue = (
	signer: string | null,
	userMap: WorkflowTemplateUserMapJSON = {}
): AssigneeValue | null => {
	if (!signer || signer === "other") return null;
	const wildcard = getAssigneeWildcard(signer);
	if (wildcard) return { id: wildcard.id, name: wildcard.name };
	return {
		id: signer,
		name: userMap[signer]?.name ?? "Unknown employee",
		active: userMap[signer]?.active ?? true,
	};
};

export type TodoRowErrors = {
	name?: string;
	assignee?: string;
	daysToComplete?: string;
	signers: (string | undefined)[];
	/** Row-level banner, e.g. the deactivated-assignee message. */
	message?: string;
};

const deactivatedMessage = (name: string): string =>
	`Assignee: ${name} has been deactivated. Please assign another employee to continue.`;

/**
 * Per-row validation for the to-dos step — the same rules the legacy page enforced
 * (no empty inputs, no deactivated assignees, 250-char labels) plus the days >= 1 floor
 * the server has always applied but the legacy client let through.
 */
export const validateTodoRow = (row: LaunchTodoRow): TodoRowErrors => {
	const errors: TodoRowErrors = { signers: row.signers.map(() => undefined) };

	const trimmedName = row.name.trim();
	if (!trimmedName) {
		errors.name = REQUIRED;
	} else if (trimmedName.length > NAME_MAX_LENGTH) {
		errors.name = `To-do name must be ${NAME_MAX_LENGTH} characters or less`;
	}

	if (row.daysToComplete === null) {
		errors.daysToComplete = REQUIRED;
	} else if (!Number.isInteger(row.daysToComplete) || row.daysToComplete < 1) {
		errors.daysToComplete = "Days to complete must be at least 1";
	}

	if (row.signatureTemplate) {
		// The launch schema requires at least one signer per signature to-do, and the row
		// renders no signer pickers when the list is empty — so without this the row looks
		// valid, blocks nothing, and 422s at launch with no field to point at.
		if (row.signers.length === 0) {
			errors.message = `${row.signatureTemplate.name} has no signature steps. Remove the document or pick a different one.`;
		}
		errors.signers = row.signers.map((signer) => {
			if (!signer) return REQUIRED;
			if (signer.active === false) return DEACTIVATED_ERROR;
			return undefined;
		});
		const deactivatedSigner = row.signers.find((signer) => signer?.active === false);
		if (deactivatedSigner) {
			errors.message = deactivatedMessage(deactivatedSigner.name);
		}
	} else if (!row.assignee) {
		errors.assignee = REQUIRED;
	} else if (row.assignee.active === false) {
		errors.assignee = DEACTIVATED_ERROR;
		errors.message = deactivatedMessage(row.assignee.name);
	}

	return errors;
};

export const todoRowHasErrors = (errors: TodoRowErrors): boolean =>
	Boolean(errors.name || errors.assignee || errors.daysToComplete || errors.message || errors.signers.some(Boolean));

export const todoRowsHaveErrors = (rows: LaunchTodoRow[]): boolean =>
	rows.some((row) => todoRowHasErrors(validateTodoRow(row)));

/**
 * Whether any to-do (or signature step) is assigned to "Employee's Manager" — this is
 * what makes the backup manager section appear, and per the launch AC a backup manager
 * is then required before the workflow can be launched.
 */
export const hasManagerAssignment = (rows: LaunchTodoRow[]): boolean =>
	rows.some(
		(row) =>
			(!row.signatureTemplate && row.assignee?.id === "manager") ||
			(row.signatureTemplate !== null && row.signers.some((signer) => signer?.id === "manager"))
	);

/** Build the legacy launch payload; `todoRowsHaveErrors` guarantees the non-null fields by now. */
export const buildLaunchPayload = ({
	generalInfo,
	userSelectionId,
	backupManager,
	rows,
}: {
	generalInfo: GeneralInfoData;
	userSelectionId: string;
	backupManager: AssigneeValue | null;
	rows: LaunchTodoRow[];
}): LaunchWorkflowPayload => ({
	name: generalInfo.workflowName.trim(),
	templateID: generalInfo.template?.id ?? "",
	// Local-offset ISO, not UTC: the server runs this through `parse_and_convert_to_utc`,
	// so dropping the offset would shift the start date a day for non-UTC users.
	// See docs/guides/date-utils.md.
	startDate: generalInfo.startDate ? formatISO(generalInfo.startDate) : "",
	userSelectionID: userSelectionId,
	defaultManagerID: backupManager?.id ?? null,
	todos: rows.map((row): LaunchWorkflowTodoJSON => {
		if (row.signatureTemplate) {
			return {
				label: row.name.trim(),
				daysToComplete: row.daysToComplete ?? 0,
				signatureRequestTemplateID: row.signatureTemplate.id,
				signers: row.signers.map((signer) => signer?.id ?? ""),
			};
		}
		return {
			label: row.name.trim(),
			daysToComplete: row.daysToComplete ?? 0,
			actor: row.assignee?.id ?? "",
		};
	}),
});
